/**
 * Locking a session that has been left sitting there.
 *
 * DECISIONS.md §19 named this as the honest gap and did not close it: "a
 * parent who leaves Valley open on the family computer and walks away has
 * left an authenticated tab sitting there, and nothing in the app times out.
 * Against the threat above -- a child who opens the browser later -- the
 * protection is total; against a child who walks up to a tab still on screen,
 * it is nothing at all."
 *
 * It is nothing at all because, until there was a PIN, there was nothing to
 * time out TO. Logging a parent out after ten quiet minutes and making them
 * fetch a password is a worse app, so the gap stayed open. A PIN changes the
 * arithmetic: the cost of locking falls to six digits, and at that price it
 * is worth doing.
 *
 * WHICH IS ALSO THE ONE RULE HERE. A parent with no PIN is never locked. For
 * them this module does nothing at all, because the only lock it could apply
 * is the one that was already rejected as too expensive. The feature buys its
 * own safety: setting a PIN is what turns the idle timer on.
 */

import { currentAccount, lock } from './cognito';
import { hasPin } from './pin';

/**
 * Ten minutes.
 *
 * Short enough that a child wandering over to an abandoned kitchen laptop
 * meets a PIN prompt, long enough that a parent reading the journal and
 * thinking about it does not get thrown out mid-sentence. Reading counts as
 * idle -- there is no mouse movement in it -- which is the argument against
 * anything much shorter.
 */
export const IDLE_MS = 10 * 60 * 1000;

/**
 * And two minutes for a tab that is not on screen at all.
 *
 * Much harsher, deliberately. A hidden tab is not being read by anybody, so
 * the "they might be thinking" defence does not apply, and a backgrounded
 * Valley on a shared machine is exactly the thing the next person finds.
 */
export const HIDDEN_MS = 2 * 60 * 1000;

/**
 * Events that mean a person is still there.
 *
 * `passive` on every one of them: these fire constantly and none of them is
 * ever cancelled here, so telling the browser that up front keeps the
 * listeners off the critical path of a scroll.
 */
const ACTIVITY = ['pointerdown', 'keydown', 'scroll', 'touchstart', 'focus'] as const;

/**
 * Start watching. Returns the function that stops watching.
 *
 * `onLock` rather than a redirect, because who renders the PIN prompt is the
 * app's business and this module should not know whether that is a screen, a
 * modal or a route.
 */
export function watchIdle(onLock: () => void): () => void {
  let timer: number | undefined;
  let hiddenAt: number | null = null;

  /** Only a locked-and-resumable account is worth locking. See the rule above. */
  function lockable(): boolean {
    const account = currentAccount();
    return account !== null && hasPin(account.sub);
  }

  function fire(): void {
    if (!lockable()) return;
    lock();
    onLock();
  }

  function restart(): void {
    window.clearTimeout(timer);
    // Armed even when there is no PIN yet, because one may be set while this
    // is running and re-arming on that event would mean this module knowing
    // when Settings saved something. `fire` re-checks, which is cheap.
    timer = window.setTimeout(fire, IDLE_MS);
  }

  function onVisibility(): void {
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now();
      return;
    }
    // Back on screen. A timer that should have fired while the tab was
    // backgrounded may not have: browsers throttle, and some suspend
    // timers entirely. So the elapsed time is measured rather than trusted
    // to a callback that might never have run.
    const away = hiddenAt === null ? 0 : Date.now() - hiddenAt;
    hiddenAt = null;
    if (away >= HIDDEN_MS) {
      fire();
      return;
    }
    restart();
  }

  for (const event of ACTIVITY) {
    window.addEventListener(event, restart, { passive: true });
  }
  document.addEventListener('visibilitychange', onVisibility);
  restart();

  return () => {
    window.clearTimeout(timer);
    for (const event of ACTIVITY) window.removeEventListener(event, restart);
    document.removeEventListener('visibilitychange', onVisibility);
  };
}
